/**
 * The operator's own clock, in the top bar, on every screen in the CRM.
 *
 * This is the half of the feature that makes the other half safe. A timer whose
 * only sign is on the task page it was started from is a timer that runs all
 * night, and the rule the server enforces at twelve hours exists precisely
 * because that happens. A counter that is visible while reading mail, taking a
 * call or building an invoice is one nobody forgets.
 *
 * IT IS DRAWN WHETHER OR NOT A CLOCK IS RUNNING, which is a deliberate reversal
 * of how this shipped. It used to appear only while timing, on the argument that
 * a permanent stopwatch teaches people to stop seeing it - and the cost of that
 * argument was that the ONLY way to start a clock was to first find the task,
 * which is the one thing an operator cannot do while they are already inside
 * something else. Perfex puts the stopwatch in the header at all times and asks
 * which task afterwards, and that is the shape asked for here.
 *
 * So the button has two faces and they are not the same control dressed twice:
 *
 *  - IDLE: a plain disc, like the mail and bell buttons beside it. It carries no
 *    counter because there is nothing to count, and its menu offers one thing.
 *  - RUNNING: the chip, with the pulsing dot, the ticking counter and the task's
 *    name. It is wider and louder than its neighbours on purpose - a running
 *    clock is the one thing in this strip that is costing money.
 *
 * The stop is one click for the ordinary case. "Stop and adjust" is the same
 * stop with the dialog opened deliberately, for the run that needs a note or a
 * figure other than the one the clock counted - a call that took ten minutes of
 * the ninety, an interruption nobody paused for.
 *
 * Starting from here opens a form rather than acting, and the form is owned by
 * `TimerProvider` rather than by this menu: the click that asks the question
 * also closes the menu, so a dialog mounted here would unmount in the same tick.
 * That is the same reason the stop and discard questions live there.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Pencil, Play, Square, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { Pop, useClosePops } from "../shell/Pop";
import { fmtDateTime } from "../ui";
import { ElapsedClock } from "./ElapsedClock";
import { useTimers } from "./TimerProvider";

export function HeaderTimer() {
  const t = useT();
  const timers = useTimers();
  const close = useClosePops();
  const mine = timers?.mine ?? null;
  if (!timers?.enabled) return null;

  const title = mine ? mine.task?.title ?? t("timer.unknownTask") : null;

  return (
    <Pop
      wide
      trigger={(open) =>
        mine ? (
          <button
            type="button"
            className={cn("crm-timer-chip", open && "is-on", mine.stale && "is-stale")}
            title={t("timer.chipTitle", { task: title! })}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <span className="crm-timer-dot" aria-hidden="true" />
            <ElapsedClock timer={mine} className="crm-timer-clock" />
            {/* The task's name is the first thing to go on a narrow screen: the
                counter is what the chip is FOR, and a title squeezed to three
                characters names nothing. */}
            <span className="crm-trunc max-lg:hidden">{title}</span>
          </button>
        ) : (
          <button
            type="button"
            className="crm-rb"
            title={t("timer.title")}
            aria-label={t("timer.title")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <Clock className="size-[18px]" strokeWidth={1.7} />
          </button>
        )
      }
    >
      {mine ? (
        <>
          <div className="crm-menu-head">
            <div className="crm-menu-t crm-trunc">{title}</div>
            <div className="crm-menu-s">{t("timer.startedAt", { at: fmtDateTime(mine.startedAt) })}</div>
          </div>

          {/* The figure the operator came here to read, drawn once and large.
              The chip's own counter is in the corner they just clicked away
              from. On a phone it is the only thing the chip has room for, and
              on the narrowest phones the chip is only its dot
              (`crm-topbar.css`) - so the menu repeats it rather than assuming
              it was seen. */}
          <div className="mx-1.5 my-2 flex items-center gap-3 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-2.5">
            <span className="crm-mut min-w-0 text-[12.5px]">{t("timer.runningFor")}</span>
            <ElapsedClock timer={mine} className="crm-push shrink-0 text-[15px] font-bold" />
          </div>

          {/* A real Link, so middle-click and "open in new tab" keep working -
              the same reason the task row menu uses one. */}
          <Link className="crm-mi" to={`/tasks/${mine.taskId}`} onClick={close}>
            <ArrowUpRight className="size-4" />
            {t("tasks.open")}
          </Link>

          <div className="crm-menu-sep" />

          <button
            type="button"
            className="crm-mi"
            disabled={timers.busy}
            onClick={() => {
              close();
              void timers.requestStop(mine);
            }}
          >
            <Square className="size-4" />
            {t("timer.stop")}
          </button>

          <button
            type="button"
            className="crm-mi"
            disabled={timers.busy}
            onClick={() => {
              close();
              timers.askStop(mine);
            }}
          >
            <Pencil className="size-4" />
            {t("timer.stopAndEdit")}
          </button>

          <div className="crm-menu-sep" />

          <button
            type="button"
            className="crm-mi crm-mi-danger"
            disabled={timers.busy}
            onClick={() => {
              close();
              timers.requestDiscard(mine);
            }}
          >
            <Trash2 className="size-4" />
            {t("timer.discard")}
          </button>
        </>
      ) : (
        <>
          <div className="crm-menu-head">
            <div className="crm-menu-t">{t("timer.title")}</div>
          </div>
          {/* Said in words rather than left blank. An empty panel reads as a
              menu that failed to load, and the sentence is also what tells the
              operator that a colleague's clock is not theirs to see here. */}
          <div className="crm-pal-empty">{t("timer.none")}</div>
          <div className="crm-menu-sep" />
          <div className="crm-menu-foot">
            <button
              type="button"
              className="crm-btn crm-btn-sm w-full justify-center"
              disabled={timers.busy}
              onClick={() => {
                close();
                timers.askStart();
              }}
            >
              <Play className="size-4" />
              {t("timer.start")}
            </button>
          </div>
        </>
      )}
    </Pop>
  );
}
